import { useCallback } from "react";
import { useQueryParam, withDefault, StringParam } from "use-query-params";

interface UseColumnSelectionOptions<Column extends string> {
  allColumns: Column[];
  defaultSelectedColumns: Column[];
  key?: string; // Optional unique key to prevent interference between multiple tables
}
type UseColumnSelectionResult<Column extends string> = {
  selectedColumns: Column[];
  setSelectedColumns: (newColumns: Column[]) => void;
  visibleColumnsFunction: () => Column[];
};

export function useColumnSelection<Column extends string>({
  allColumns,
  defaultSelectedColumns,
  key,
}: UseColumnSelectionOptions<Column>): UseColumnSelectionResult<Column> {
  // Use query params for persistence with unique key per table
  // Default to "columns" for backward compatibility
  const queryParamKey = key ? `columns-${key}` : "columns";
  const [querySelectedColumns, setQuerySelectedColumns] = useQueryParam(
    queryParamKey,
    withDefault(StringParam, ""),
  );

  const selectedColumns =
    querySelectedColumns && querySelectedColumns.length > 0
      ? (querySelectedColumns
          .split(",")
          .filter((col) => allColumns.includes(col as Column)) as Column[])
      : defaultSelectedColumns;

  const setSelectedColumns = useCallback(
    (newColumns: Column[]) => {
      const sortedColumns = [...newColumns].sort(
        (a, b) => allColumns.indexOf(a) - allColumns.indexOf(b),
      );
      setQuerySelectedColumns(sortedColumns.join(","));
    },
    [allColumns, setQuerySelectedColumns],
  );

  const visibleColumnsFunction = useCallback(
    () => selectedColumns,
    [selectedColumns],
  );

  return {
    selectedColumns,
    setSelectedColumns,
    visibleColumnsFunction,
  };
}
